import React, { useState, useEffect } from 'react';
import { Combobox } from '@headlessui/react';
import { Check, ChevronDown, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { ContractWizardState, SchemaDefinition } from '../../../types/contract';
import { Integration } from '../../../types/integration'; // Import Integration from the correct path
import { fetchAllSchemaDefinitions } from '../../../services/schemaDefinitionApiService';
import { integrationService } from '../../../services/integration.service';

interface ContractDetailsStepProps {
  state: ContractWizardState;
  updateState: (updates: Partial<ContractWizardState>) => void;
}

const ContractDetailsStep: React.FC<ContractDetailsStepProps> = ({ state, updateState }) => {
  const [schemaDefinitions, setSchemaDefinitions] = useState<SchemaDefinition[]>([]);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [isLoadingSchemas, setIsLoadingSchemas] = useState(true);
  const [isLoadingIntegrations, setIsLoadingIntegrations] = useState(true);
  const [schemaQuery, setSchemaQuery] = useState('');
  const [integrationQuery, setIntegrationQuery] = useState('');
  
  // Load schema definitions and integrations
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoadingSchemas(true);
        setIsLoadingIntegrations(true);
        
        const [schemasData, integrationsResponse] = await Promise.all([
          fetchAllSchemaDefinitions(),
          integrationService.getIntegrations()
        ]);
        const integrationsData = integrationsResponse.data;
        
        setSchemaDefinitions(schemasData);
        setIntegrations(integrationsData);
      } catch (error: any) {
        console.error('Failed to load data in ContractDetailsStep:', error);
        // Optionally set an error state to display a message to the user
        // setError(error.message || 'An unknown error occurred while loading data.');
      } finally {
        setIsLoadingSchemas(false);
        setIsLoadingIntegrations(false);
      }
    };
    
    loadData();
  }, []);
  
  // Filter schema definitions based on search query
  const filteredSchemas = schemaQuery === ''
    ? schemaDefinitions
    : schemaDefinitions.filter(schema => 
        schema.name.toLowerCase().includes(schemaQuery.toLowerCase()) ||
        schema.codeName.toLowerCase().includes(schemaQuery.toLowerCase())
      );
  
  // Filter integrations based on search query
  const filteredIntegrations = integrationQuery === ''
    ? integrations
    : integrations.filter(integration => 
        integration.name.toLowerCase().includes(integrationQuery.toLowerCase())
      );
  
  // Get selected schemas
  const selectedSchemas = schemaDefinitions.filter(schema => 
    state.selectedSchemaIds.includes(schema.id)
  );
  
  // Get selected integrations
  const selectedIntegrations = integrations.filter(integration => 
    state.selectedIntegrationIds.includes(integration.id)
  );
  
  // Handle schema selection
  const handleSchemaSelect = (schemaId: string) => {
    const isSelected = state.selectedSchemaIds.includes(schemaId);
    
    if (isSelected) {
      updateState({
        selectedSchemaIds: state.selectedSchemaIds.filter(id => id !== schemaId)
      });
    } else {
      updateState({
        selectedSchemaIds: [...state.selectedSchemaIds, schemaId]
      });
    }
  };
  
  // Handle integration selection
  const handleIntegrationSelect = (integrationId: string) => {
    const isSelected = state.selectedIntegrationIds.includes(integrationId);
    
    if (isSelected) {
      updateState({
        selectedIntegrationIds: state.selectedIntegrationIds.filter(id => id !== integrationId)
      });
    } else {
      updateState({
        selectedIntegrationIds: [...state.selectedIntegrationIds, integrationId]
      });
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-white dark:bg-gray-800 dark:border dark:border-gray-700 rounded-lg shadow-md p-10 max-w-3xl mx-auto" // Added max-w-3xl and mx-auto
    >
      <div className="space-y-8">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">Contract Details</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-8">
            Provide basic information about your data contract and select the schema definitions and integrations it will use.
          </p>
        </div>
        
        {/* Contract Name */}
        <div>
          <label htmlFor="contract-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Contract Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="contract-name"
            value={state.name}
            onChange={(e) => updateState({ name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:focus:border-blue-600 focus:ring-blue-500 focus:border-blue-500 rounded-md focus:ring focus:outline-none dark:focus:ring-blue-500/20 dark:bg-gray-800 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out"
            placeholder="Enter contract name"
            required
          />
          {!state.name && (
            <p className="mt-1 text-sm text-red-500">Contract name is required</p>
          )}
        </div>
        
        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Description
          </label>
          <textarea
            id="description"
            value={state.description}
            onChange={(e) => updateState({ description: e.target.value })}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:focus:border-blue-600 focus:ring-blue-500 focus:border-blue-500 rounded-md focus:ring focus:outline-none dark:focus:ring-blue-500/20 dark:bg-gray-800 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out"
            placeholder="Enter a description for this contract"
          />
        </div>
        
        {/* Schema Definitions Multi-select */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Schema Definitions <span className="text-red-500">*</span>
          </label>
          <Combobox
            as="div"
            className="relative"
            value={state.selectedSchemaIds}
            onChange={(value) => updateState({ selectedSchemaIds: value })}
            multiple
          >
            <div className="relative w-full">
              <div className="relative w-full cursor-default rounded-md border px-3 py-2 text-left shadow-sm focus-within:outline-none focus-within:ring-1 sm:text-sm border-gray-300 dark:border-gray-600 dark:focus-within:border-blue-600 focus-within:ring-blue-500 focus-within:border-blue-500 dark:focus-within:ring-blue-500/20 dark:bg-gray-800 text-gray-900 dark:text-gray-100 transition duration-300 ease-in-out">
                <div className="flex flex-wrap gap-1 pr-8">
                  {selectedSchemas.map(schema => (
                    <span
                      key={schema.id}
                      className="inline-flex items-center rounded-full bg-blue-200 px-2.5 py-0.5 text-xs font-medium text-blue-900 dark:bg-blue-700 dark:text-blue-100"
                    >
                      {schema.name}
                      <button
                        type="button"
                        onClick={() => handleSchemaSelect(schema.id)}
                        className="ml-1 -mr-0.5 h-4 w-4 flex-shrink-0 rounded-full hover:bg-blue-300 dark:hover:bg-blue-600 inline-flex items-center justify-center text-blue-900 dark:text-blue-100"
                      >
                        <span className="sr-only">Remove {schema.name}</span>
                        <X className="h-2 w-2" />
                      </button>
                    </span>
                  ))}
                  <Combobox.Input
                    className="flex-1 min-w-0 bg-transparent border-none focus:ring-0 focus:outline-none text-sm placeholder-gray-400 dark:placeholder-gray-500"
                    onChange={(e) => setSchemaQuery(e.target.value)}
                    onFocus={() => setSchemaQuery('')} // Show all options on focus
                    placeholder={selectedSchemas.length === 0 ? "Search schema definitions" : ""}
                    displayValue={() => ''} // Always return empty string as chips are rendered explicitly
                  />
                </div>
                <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronDown className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </Combobox.Button>
              </div>
              
              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-900 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                {isLoadingSchemas ? (
                  <div className="py-2 px-4 text-gray-500 dark:text-gray-400">Loading...</div>
                ) : filteredSchemas.length === 0 && schemaQuery !== '' ? (
                  <div className="py-2 px-4 text-gray-500 dark:text-gray-400">No schema definitions found for "{schemaQuery}"</div>
                ) : filteredSchemas.length === 0 && schemaQuery === '' ? (
                  <div className="py-2 px-4 text-gray-500 dark:text-gray-400">No schema definitions available</div>
                ) : (
                  filteredSchemas.map(schema => {
                    const isSelected = state.selectedSchemaIds.includes(schema.id);
                    return (
                      <Combobox.Option
                        key={schema.id}
                        value={schema.id}
                        className={({ active }) =>
                          `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                            active ? 'bg-blue-600 text-white' : 'text-gray-900 dark:text-white'
                          }`
                        }
                      >
                        {({ active }) => (
                          <>
                            <span className={`block truncate ${isSelected ? 'font-medium' : 'font-normal'}`}>
                              {schema.name}
                            </span>
                            {isSelected && (
                              <span
                                className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                  active ? 'text-white' : 'text-blue-600 dark:text-blue-400'
                                }`}
                              >
                                <Check className="h-5 w-5" aria-hidden="true" />
                              </span>
                            )}
                          </>
                        )}
                      </Combobox.Option>
                    );
                  })
                )}
              </Combobox.Options>
            </div>
          </Combobox>
          {state.selectedSchemaIds.length === 0 && (
            <p className="mt-1 text-sm text-red-500">At least one schema definition is required</p>
          )}
        </div>
        
        {/* Integrations Multi-select */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Integrations
          </label>
          <Combobox
            as="div"
            className="relative"
            value={state.selectedIntegrationIds}
            onChange={(value) => updateState({ selectedIntegrationIds: value })}
            multiple
          >
            <div className="relative w-full">
              <div className="relative w-full cursor-default rounded-md border px-3 py-2 text-left shadow-sm focus-within:outline-none focus-within:ring-1 sm:text-sm border-gray-300 dark:border-gray-600 dark:focus-within:border-blue-600 focus-within:ring-blue-500 focus-within:border-blue-500 dark:focus-within:ring-blue-500/20 dark:bg-gray-800 text-gray-900 dark:text-gray-100 transition duration-300 ease-in-out">
                <div className="flex flex-wrap gap-1 pr-8">
                  {selectedIntegrations.map(integration => (
                    <span
                      key={integration.id}
                      className="inline-flex items-center rounded-full bg-green-200 px-2.5 py-0.5 text-xs font-medium text-green-900 dark:bg-green-700 dark:text-green-100"
                    >
                      <img 
                        src={integration.logo_url} 
                        alt={integration.name} 
                        className="h-4 w-4 mr-1.5"
                      />
                      {integration.name}
                      <button
                        type="button"
                        onClick={() => handleIntegrationSelect(integration.id)}
                        className="ml-1 -mr-0.5 h-4 w-4 flex-shrink-0 rounded-full hover:bg-green-300 dark:hover:bg-green-600 inline-flex items-center justify-center text-green-900 dark:text-green-100"
                      >
                        <span className="sr-only">Remove {integration.name}</span>
                        <X className="h-2 w-2" />
                      </button>
                    </span>
                  ))}
                  <Combobox.Input
                    className="flex-1 min-w-0 bg-transparent border-none focus:ring-0 focus:outline-none text-sm placeholder-gray-400 dark:placeholder-gray-500"
                    onChange={(e) => setIntegrationQuery(e.target.value)}
                    onFocus={() => setIntegrationQuery('')} // Show all options on focus
                    placeholder={selectedIntegrations.length === 0 ? "Search integrations" : ""}
                    displayValue={() => ''} // Always return empty string as chips are rendered explicitly
                  />
                </div>
                <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronDown className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </Combobox.Button>
              </div>
              
              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-900 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                {isLoadingIntegrations ? (
                  <div className="py-2 px-4 text-gray-500 dark:text-gray-400">Loading...</div>
                ) : filteredIntegrations.length === 0 && integrationQuery !== '' ? (
                  <div className="py-2 px-4 text-gray-500 dark:text-gray-400">No integrations found for "{integrationQuery}"</div>
                ) : filteredIntegrations.length === 0 && integrationQuery === '' ? (
                  <div className="py-2 px-4 text-gray-500 dark:text-gray-400">No integrations available</div>
                ) : (
                  filteredIntegrations.map(integration => {
                    const isSelected = state.selectedIntegrationIds.includes(integration.id);
                    return (
                      <Combobox.Option
                        key={integration.id}
                        value={integration.id}
                        className={({ active }) =>
                          `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                            active ? 'bg-blue-600 text-white' : 'text-gray-900 dark:text-white'
                          }`
                        }
                      >
                        {({ active }) => (
                          <>
                            <span className={`block truncate ${isSelected ? 'font-medium' : 'font-normal'} flex items-center`}>
                              <img 
                                src={integration.logo_url} 
                                alt={integration.name} 
                                className="h-5 w-5 mr-2"
                              />
                              {integration.name}
                            </span>
                            {isSelected && (
                              <span
                                className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                  active ? 'text-white' : 'text-blue-600 dark:text-blue-400'
                                }`}
                              >
                                <Check className="h-5 w-5" aria-hidden="true" />
                              </span>
                            )}
                          </>
                        )}
                      </Combobox.Option>
                    );
                  })
                )}
              </Combobox.Options>
            </div>
          </Combobox>
        </div>
      </div>
    </motion.div>
  );
};

export default ContractDetailsStep;
